import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiClock, FiUser, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * AuditLogPanel - Chronological audit trail for a brokerage request.
 *
 * Toggleable from footer of RequestWorkflowModal.
 * Shows all state transitions, edits, and actions with timestamps and actors.
 */

const ACTION_LABELS = {
  'STATUS_TRANSITION': 'Status Transition',
  'FIELD_UPDATE': 'Field Updated',
  'CANDIDATE_SURFACED': 'Candidates Surfaced',
  'CANDIDATE_SELECTED': 'Candidate Selected',
  'CANDIDATE_SKIPPED': 'Candidate Skipped',
  'OUTREACH_CREATED': 'Outreach Created',
  'OUTREACH_RESPONSE': 'Outreach Response',
  'CONSENT_REQUESTED': 'Consent Requested',
  'CONSENT_RESPONDED': 'Consent Response',
  'SESSION_SCHEDULED': 'Session Scheduled',
  'SESSION_COMPLETED': 'Session Completed',
  'SESSION_RESCHEDULED': 'Session Rescheduled',
  'SESSION_CANCELLED': 'Session Cancelled',
  'REQUEST_CLOSED': 'Request Closed',
  'REQUEST_CLAIMED': 'Request Claimed',
  'REQUEST_CREATED': 'Request Created',
  'BULK_CLOSE': 'Bulk Close',
  'PRIORITY_UPDATED': 'Priority Updated',
};

const ACTION_COLORS = {
  'STATUS_TRANSITION': 'bg-blue-100 text-blue-700',
  'CANDIDATE_SELECTED': 'bg-purple-100 text-purple-700',
  'CONSENT_RESPONDED': 'bg-green-100 text-green-700',
  'SESSION_COMPLETED': 'bg-green-100 text-green-700',
  'REQUEST_CLOSED': 'bg-gray-100 text-gray-700',
  'OUTREACH_CREATED': 'bg-indigo-100 text-indigo-700',
};

const AuditLogPanel = ({ requestId, authHeaders }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAuditLog = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/audit-log`,
        authHeaders()
      );
      setEntries(res.data.items || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
  }, [requestId]);

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    return ACTION_LABELS[action] || action?.replace(/_/g, ' ') || 'Unknown';
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || 'bg-gray-100 text-gray-600';
  };

  const renderStateChange = (entry) => {
    const before = entry.before_state;
    const after = entry.after_state;

    if (!before && !after) return null;

    // Status transition
    if (before?.status && after?.status && before.status !== after.status) {
      return (
        <div className="flex items-center gap-1.5 text-xs mt-1">
          <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">{before.status}</span>
          <FiArrowRight className="w-3 h-3 text-gray-400" />
          <span className="px-1.5 py-0.5 bg-indigo-200 text-indigo-700 rounded">{after.status}</span>
        </div>
      );
    }

    // Generic field changes
    const changes = [];
    if (after) {
      Object.keys(after).forEach(key => {
        if (key === 'status') return;
        const beforeVal = before ? before[key] : undefined;
        const afterVal = after[key];
        if (beforeVal !== afterVal) {
          changes.push({ key, before: beforeVal, after: afterVal });
        }
      });
    }

    if (changes.length === 0) return null;

    return (
      <div className="mt-1 space-y-0.5">
        {changes.slice(0, 3).map((change, idx) => (
          <div key={idx} className="text-xs text-gray-500">
            <span className="font-medium">{change.key.replace(/_/g, ' ')}</span>
            {change.before !== undefined && (
              <span>: <span className="line-through text-gray-400">{String(change.before)}</span></span>
            )}
            <span> → <span className="text-gray-700">{String(change.after ?? '')}</span></span>
          </div>
        ))}
        {changes.length > 3 && (
          <span className="text-xs text-gray-400">+{changes.length - 3} more changes</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Audit Log</h3>
        <button
          onClick={fetchAuditLog}
          disabled={loading}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">{error}</div>
      )}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
          <span className="ml-2 text-xs text-gray-500">Loading audit log...</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-6 text-xs text-gray-400">No audit entries found.</div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-3 top-3 bottom-3 w-px bg-gray-200" />

          <div className="space-y-3">
            {entries.map((entry, idx) => (
              <div key={entry.id || idx} className="relative flex gap-3 pl-1">
                {/* Timeline dot */}
                <div className="w-6 h-6 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center flex-shrink-0 z-10">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getActionColor(entry.action)}`}>
                      {getActionLabel(entry.action)}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <FiClock className="w-3 h-3" />
                      {formatTimestamp(entry.created_at)}
                    </span>
                  </div>

                  {/* Actor */}
                  {entry.actor_id && (
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                      <FiUser className="w-3 h-3" />
                      <span>{entry.actor_type}: {entry.actor_id?.substring(0, 8)}...</span>
                    </div>
                  )}

                  {/* State changes */}
                  {renderStateChange(entry)}

                  {/* Metadata */}
                  {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                    <div className="mt-1 text-xs text-gray-500">
                      {Object.entries(entry.metadata).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-3">
                          {k.replace(/_/g, ' ')}: <span className="text-gray-700">{String(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPanel;
